import type { RegimeBySector } from "@eu.mdotm.api/sphere";
import { IconWalls } from "$root/components/IconWall";
import { hmmRepository } from "$root/functional-areas/hmm/repository";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { builtInCaseInsensitiveSort, type ContextContent, groupMap, objectCoalesce, withContext } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { RegionContext } from "$root/widgets-architecture/contexts/region";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { type ClassList, overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { groupBy } from "@mdotm/utils/collections";
import Color from "color";
import type { Chart } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, type ReactNode, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

const phaseValueMap = {
	[-1]: "uncertain",
	1: "growth",
	2: "lateral",
	3: "highStress",
} as const;

type MarketRegimes = (typeof phaseValueMap)[keyof typeof phaseValueMap];

type HistogramChartProps = {
	series: {
		name: MarketRegimes;
		quantity: number;
	}[];
	groups: {
		uncertain: (keyof RegimeBySector)[];
		growth: (keyof RegimeBySector)[];
		lateral: (keyof RegimeBySector)[];
		highStress: (keyof RegimeBySector)[];
	};
};

const colorByPhase: Record<MarketRegimes, string> = {
	growth: "#4CB09C",
	lateral: "#FFBF00",
	highStress: "#FF1D23",
	uncertain: "#8C8EA8",
};

function LegendList({ children, coloumcount }: { children: ReactNode; coloumcount: number }): React.JSX.Element {
	return <div className={`columns-${coloumcount}`}>{children}</div>;
}

export type LegendItemProps = {
	text: string;
	className?: ClassList;
	color: string;
};

function LegendItem({ text, className, color }: LegendItemProps): React.JSX.Element {
	return (
		<span className={overrideClassName("flex items-center h-[24px] text-[11px]", className)}>
			<Icon icon="driver-arrow-center" className="mr-[8px]" size={18} color={color} />
			{text}
		</span>
	);
}

const HistogramChart = forwardRef<{ chart: Chart }, HistogramChartProps>(function HistogramChartInner(
	{ series, groups },
	ref,
) {
	const { t } = useTranslation();

	const titleByPhase = useMemo(
		() =>
			Object.fromEntries(
				Object.values(phaseValueMap).map((phase) => [phase, t(`REGIME_COUNTER.OPTIONS.${phase}`)]),
			) as Record<MarketRegimes, string>,
		[t],
	);

	const countLegendaColumns = useMemo(() => {
		const legendaSum = series.reduce((acc, { name }) => {
			return acc + groups[name].length;
		}, 0);
		return Math.round(legendaSum / 5);
	}, [series, groups]);

	const sortedLegendaRegime = useMemo(() => {
		return series.reduce((acc: Array<{ selectorRegime: keyof RegimeBySector; groupName: MarketRegimes }>, { name }) => {
			return [
				...acc,
				...[...groups[name]]
					.sort((a, b) => builtInCaseInsensitiveSort(a, b))
					.map((regime: keyof RegimeBySector) => ({ selectorRegime: regime, groupName: name })),
			];
		}, []);
	}, [series, groups]);

	const countersOptions = useMemo(() => {
		return {
			chart: {
				type: "bar",
				style: {
					fontFamily: "Gotham,sans-serif",
				},
				height: 220,
			},
			credits: {
				enabled: false,
			},
			panning: false,
			title: { text: undefined },
			subtitle: { text: undefined },
			tooltip: {
				enabled: false,
			},
			legend: {
				enabled: false,
			},
			xAxis: {
				type: "category",
				categories: series.map(({ name }) => name),
				title: {
					text: "",
				},
				visible: true,
				labels: {
					enabled: true,
					align: "left",
					reserveSpace: true,
					useHTML: true,
					formatter: ({ value }: { value: MarketRegimes }) => {
						return `<span style="color: ${colorByPhase[value]}; font-weight: bold">${titleByPhase[value]}</span>`;
					},
				},
			},
			yAxis: {
				min: 0,
				title: {
					text: "",
				},
				visible: true,
				labels: { enabled: false },
				gridLineColor: Color("#D1D2DC").alpha(0.6).toString(),
				gridLineWidth: 2,
				tickLength: 0,
				tickInterval: 1,
				//gridZIndex: 4,
				gridLineDashStyle: "ShortDash",
			},
			plotOptions: {
				bar: {
					pointWidth: undefined,
					borderWidth: 0,
					dataLabels: {
						enabled: true,
						color: "#616161",
					},
					colors: series.map(({ name }) => Color(colorByPhase[name]).alpha(0.5).toString()),
				},
				series: {
					groupPadding: 0,
					pointPadding: 0.2,
				},
			},
			series: [
				{
					colorByPoint: true,
					data: series.map(({ quantity }) => quantity),
				},
			],
			exporting: {
				enabled: false,
			},
		};
	}, [series, titleByPhase]);

	return (
		<div className="flex flex-col min-h-0">
			<div
				style={{
					borderRadius: 4,
					borderColor: "#eeeef1",
					borderWidth: 1,
					borderStyle: "solid",
					flex: 0,
					marginBottom: "0.75rem",
				}}
			>
				<HighchartsReact
					containerProps={{ style: { height: 220 } }}
					highcharts={Highcharts}
					constructorType="chart"
					options={countersOptions}
					ref={ref as any}
				/>
			</div>
			<div className="flex-1 min-h-0 overflow-auto">
				<LegendList coloumcount={countLegendaColumns}>
					{sortedLegendaRegime.map(({ groupName, selectorRegime }) => (
						<LegendItem
							key={selectorRegime}
							className={groupName}
							color={colorByPhase[groupName]}
							text={t(`REGIME_COUNTER.SECTORS.${selectorRegime}`)}
						/>
					))}
				</LegendList>
			</div>
		</div>
	);
});

const CurrentRegimeCounterBlock = (props: ContextContent<typeof RegionContext>) => {
	const { t } = useTranslation();
	const region = props.region;

	const sectorsWithRegimeQuery = hmmRepository.sectorsWithRegime.useQuery(region);

	const {
		isLoading,
		isError,
		isFetching,
		data: hmmRegimeData,
	} = useMappedQuery(
		(sectorsWithRegime) => {
			const sectorsByGroup = groupMap(
				objectCoalesce(
					Object.values(phaseValueMap),
					groupBy(Object.entries(sectorsWithRegime) as [keyof RegimeBySector, -1 | 1 | 2 | 3][], ([, phase]) => {
						return phaseValueMap[phase];
					}),
					() => [],
				),
				([sector]) => sector,
			);

			return {
				phaseCounters: (Object.entries(sectorsByGroup) as [MarketRegimes, (keyof RegimeBySector)[]][]).map(
					([group, sectors]) => ({
						name: group,
						quantity: sectors.length,
					}),
				),
				sectorsByGroup,
			};
		},
		[],
		sectorsWithRegimeQuery,
	);

	useWidgetOptions(
		() => ({
			title: t("REGIME_COUNTER.TITLE"),
			subTitle: <Trans i18nKey="REGIME_COUNTER.DESCRIPTION" />,
		}),
		[t],
	);

	return (
		<>
			{isError ? (
				<IconWalls.ErrorData />
			) : isLoading || isFetching ? (
				<IconWalls.LoadingData />
			) : (
				<div className="flex flex-col min-h-0">
					{/* <div style={{ height: "100%", background: "yellow" }}></div> */}
					<HistogramChart series={hmmRegimeData.phaseCounters} groups={hmmRegimeData.sectorsByGroup} />
					{/* <div style={{ flex: 1, paddingTop: "1rem" }}>
							<Grid container spacing={2}>
								<Grid xs={6} item>
									<Section hmmRegimeData={hmmRegimeData} type="growth" />
									<Section hmmRegimeData={hmmRegimeData} type="highStress" />
								</Grid>
								<Grid xs={6} item>
									<Section hmmRegimeData={hmmRegimeData} type="lateral" />
									<Section hmmRegimeData={hmmRegimeData} type="uncertain" />
								</Grid>
							</Grid>
						</div> */}
				</div>
			)}
		</>
	);
};

export default withContext(RegionContext)(CurrentRegimeCounterBlock);
